import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Box } from '@mui/material';
import { ServiceType, YesOrNoAnswer } from '@/__generated__/globalTypes';
import { useAppDispatch, useSeekerState, useSeekerCCState } from '@/components/AppState';
import OverlaySpinner from '@/components/OverlaySpinner';
import { useHideEnrollmentSteps } from '@/components/hooks/useHideEnrollmentSteps';
import useIsCCSkipCareKind from '@/components/hooks/useIsCCSkipCareKind';
import useIsSeekerEnrollment2 from '@/components/hooks/useIsSeekerEnrollment2';
import useIsSinglePageEnrollment from '@/components/hooks/useIsSinglePageEnrollment';
import useQueryParamData from '@/components/hooks/useQueryParamData';
import {
  useRetrieveSeoMfeSharedData,
  useSeoMfeSharedData,
} from '@/components/hooks/useSeoMfeSharedData';
import {
  useRetrieveVhpMfeSharedData,
  useVhpMfeSharedData,
} from '@/components/hooks/useVhpMfeSharedData';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import FullWidthLayout from '@/components/layouts/FullWidthLayout';
import { logger } from '@sentry/utils';
import { CLIENT_FEATURE_FLAGS, FLOWS, comesFromVhp } from '@/constants';
import CareLocation from '@/pages/seeker/cc/care-location';
import { ServiceIdsForMember, TimeIntent, DefaultCareKind } from '@/types/seekerCC';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';
import withFlowStartPoint from '@/utilities/withFlowStartPoint';
import SignUpPage from '@/components/pages/sign-up';
import { SxClassProps } from '@/types/common';
import { FeatureFlagSharedDataNames } from '@/types/featureFlagSharedData';
import CareDatePage from '../care-date/care-date';
import CareKindPage from '../care-kind/care-kind';

const classes: SxClassProps = {
  wrapper: (theme) => ({
    height: 'calc(100vh - 105px)',
    [theme.breakpoints.up('md')]: {
      // accounting for header height
      height: `calc(100vh - 152px)`,
    },
  }),
};

function CareIndex() {
  const dispatch = useAppDispatch();
  const { query: { intent, serviceIdForMember, zipcode: zipcodeQueryParam } = {} } = useRouter();
  const { isUserAssignedToSkipSteps } = useSeekerState();

  const { careKind } = useSeekerCCState();
  const isDayCare = careKind === DefaultCareKind.DAY_CARE_CENTERS;

  const { isFetching } = useVhpMfeSharedData();
  const { data: vhpMfeData, isFetching: isFetchingVhpMfeSharedData } =
    useRetrieveVhpMfeSharedData();
  const {
    zipcodeData: zipcodeDataFromVhpMfe,
    comeFrom,
    loginLinkViewedTestFired,
  } = vhpMfeData || {};

  const { isFetching: isFetchingSeoMfeData } = useSeoMfeSharedData();
  const { data: seoMfeData } = useRetrieveSeoMfeSharedData();
  const { zipcodeData: zipcodeDataFromSeoMfe } = seoMfeData || {};

  const { seekerCCSkipCareKindFlag, isCCSkipCareKind, isCCSkipCareKindEligible } =
    useIsCCSkipCareKind();

  const {
    isSeekerSinglePageEnrollmentEligible,
    seekerSinglePageEnrollmentFlag,
    seekerSEMSinglePageEnrollmentFlag,
    isSeekerSinglePageEnrollment,
    isSEMSeekerSinglePageEnrollmentEligible,
  } = useIsSinglePageEnrollment();
  const { seekerEnrollment2FlagName, seekerEnrollment2Flag, isSeekerEnrollment2Eligible } =
    useIsSeekerEnrollment2(ServiceType.CHILD_CARE);

  useQueryParamData();
  const { isUserEligibleForTest } = useHideEnrollmentSteps();
  const loading =
    isFetching ||
    isFetchingSeoMfeData ||
    isUserEligibleForTest === undefined ||
    (isUserEligibleForTest && isUserAssignedToSkipSteps === undefined);

  const isZipCodeSupplied = zipcodeDataFromSeoMfe?.zipcode || zipcodeQueryParam;

  const shouldSkipLocation =
    zipcodeDataFromVhpMfe?.zipcode !== '' &&
    ((comeFrom === comesFromVhp && zipcodeDataFromVhpMfe?.zipcode) ||
      (isUserAssignedToSkipSteps === YesOrNoAnswer.YES && isZipCodeSupplied));

  useEffect(() => {
    if (isCCSkipCareKindEligible) {
      AnalyticsHelper.logTestExposure(
        CLIENT_FEATURE_FLAGS.SEEKER_CC_SKIP_CARE_KIND_SEO,
        seekerCCSkipCareKindFlag
      );
    }
  }, [isCCSkipCareKindEligible]);

  useEffect(() => {
    if (isSeekerSinglePageEnrollmentEligible) {
      if (isSEMSeekerSinglePageEnrollmentEligible) {
        AnalyticsHelper.logTestExposure(
          CLIENT_FEATURE_FLAGS.MARTECH_SINGLE_PAGE_ENROLLMENT_MARKETING_MFE,
          seekerSEMSinglePageEnrollmentFlag
        );
      } else {
        AnalyticsHelper.logTestExposure(
          CLIENT_FEATURE_FLAGS.GROWTH_SEO_SEEKER_SINGLE_PAGE_ENROLLMENT,
          seekerSinglePageEnrollmentFlag
        );
      }
    }
  }, [isSeekerSinglePageEnrollmentEligible]);

  useEffect(() => {
    if (seekerEnrollment2FlagName && serviceIdForMember !== ServiceIdsForMember.dayCare) {
      AnalyticsHelper.logTestExposure(seekerEnrollment2FlagName, seekerEnrollment2Flag);
      dispatch({
        type: 'updateFeatureFlagSharedData',
        flagName: seekerEnrollment2FlagName as FeatureFlagSharedDataNames,
        flagValue: seekerEnrollment2Flag?.value,
      });
    }
  }, [isSeekerEnrollment2Eligible]);

  useEffect(() => {
    // GROW-1286: Previous intents should be cleared every time a user reaches the enrollment flow.
    // We set them even if they're undefined so they are reset to their default values
    dispatch({ type: 'setCzenIntent', intent: intent as TimeIntent });
    dispatch({
      type: 'setCzenServiceIdForMember',
      serviceIdForMember: serviceIdForMember as ServiceIdsForMember,
    });
  }, []);

  useEffect(() => {
    if (isCCSkipCareKind) {
      dispatch({ type: 'setFlowName', flowName: FLOWS.SEEKER_CHILD_CARE_SKIP_CARE_KIND_SEO.name });
    }
    if (isSeekerEnrollment2Eligible) {
      if (isDayCare) {
        dispatch({ type: 'setFlowName', flowName: FLOWS.SEEKER_DAYCARE_CHILD_CARE_REDESIGN.name });
      } else {
        dispatch({ type: 'setFlowName', flowName: FLOWS.SEEKER_CHILD_CARE_REDESIGN.name });
      }
    }
  }, [loading, isCCSkipCareKind]);

  useEffect(() => {
    if (
      isSeekerSinglePageEnrollment ||
      !isFetchingSeoMfeData ||
      ((comeFrom === comesFromVhp || loginLinkViewedTestFired) && !isFetchingVhpMfeSharedData)
    ) {
      let screenName = 'Care Location';

      if (shouldSkipLocation) {
        screenName = 'Care Dates';
      }
      if (isSeekerSinglePageEnrollment) {
        screenName = 'Single Page Enrollment';
      }

      if (isSeekerEnrollment2Eligible) {
        screenName = 'Care Kind';
      }

      AnalyticsHelper.logEvent({
        name: 'Screen Viewed - Seeker Enrollment',
        data: {
          screen_name: screenName,
          referer: document.referrer,
        },
      });

      logger.info({
        event: 'Screen Viewed - Seeker Enrollment',
        screenName,
        vertical: 'Childcare',
        url: window.location.href,
      });
    }
  }, [
    comeFrom,
    loginLinkViewedTestFired,
    isSeekerSinglePageEnrollment,
    isFetchingVhpMfeSharedData,
    isFetchingSeoMfeData,
  ]);

  if (loading) {
    return <OverlaySpinner isOpen={isFetching || isFetchingSeoMfeData} wrapped />;
  }

  if (shouldSkipLocation && !isSeekerEnrollment2Eligible) {
    return (
      <DefaultLayout>
        <Box sx={classes.wrapper}>
          <CareDatePage />
        </Box>
      </DefaultLayout>
    );
  }

  if (isSeekerSinglePageEnrollment && !isSeekerEnrollment2Eligible) {
    return <SignUpPage />;
  }

  if (isSeekerEnrollment2Eligible) {
    return (
      <DefaultLayout>
        <Box sx={classes.wrapper}>
          <CareKindPage />
        </Box>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <Box sx={classes.wrapper}>
        <CareLocation />
      </Box>
    </DefaultLayout>
  );
}

CareIndex.disableScreenViewed = true;
CareIndex.removeBGWrapperClass = true;
CareIndex.Layout = FullWidthLayout;

export default withFlowStartPoint(CareIndex);
